import { createContext, useCallback, useContext, useEffect, useId, useRef } from "react";
import type { PropsWithChildren } from "react";
import { useBlocker } from "react-router-dom";
import type { BlockerFunction } from "react-router-dom";

type Guard = { message: string; locked: boolean };
type GuardContext = {
  register: (id: string, guard: Guard) => () => void;
  canLeave: () => boolean;
};
const Context = createContext<GuardContext | null>(null);

/** One router blocker also protects non-route exits such as switching class. */
export function PageExitGuardProvider({ children }: PropsWithChildren) {
  const guards = useRef(new Map<string, Guard>());
  const register = useCallback((id: string, guard: Guard) => {
    guards.current.set(id, guard);
    return () => { guards.current.delete(id); };
  }, []);
  const canLeave = useCallback(() => {
    const entries = [...guards.current.values()];
    const locked = entries.find((entry) => entry.locked);
    if (locked) { window.alert(locked.message); return false; }
    return entries.length === 0 || window.confirm(entries.map((entry) => entry.message).join("\n"));
  }, []);
  const shouldBlock = useCallback<BlockerFunction>(({ currentLocation, nextLocation }) =>
    guards.current.size > 0 && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search), []);
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (canLeave()) blocker.proceed();
    else blocker.reset();
  }, [blocker, canLeave]);
  useEffect(() => {
    const prevent = (event: BeforeUnloadEvent) => {
      if (!guards.current.size) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", prevent);
    return () => window.removeEventListener("beforeunload", prevent);
  }, []);
  return <Context.Provider value={{ register, canLeave }}>{children}</Context.Provider>;
}

export function usePageExitGuard(active: boolean, message: string, locked = false) {
  const context = useContext(Context);
  const id = useId();
  const register = context?.register;
  useEffect(() => {
    if (active) return register?.(id, { message, locked });
  }, [active, id, register, message, locked]);
}

export function useCanLeavePage() {
  const context = useContext(Context);
  if (!context) throw new Error("PageExitGuardProvider is required");
  return context.canLeave;
}
